import { dataModules } from '../db/data';
import drinksData, { drinkBrands } from '../db/data/drinksData';
import snacksData, { snackBrands } from '../db/data/snacksData';
import { applyOverridesToData, collectAvailabilityFields, collectPriceFields, isItemAvailable } from './priceOverrides';
import { appendCustomItems, ITEM_CATEGORIES } from './menuContent';

const LABELS = {
  ...ITEM_CATEGORIES, breads: 'Sandwich breads', cheeses: 'Sandwich cheeses',
  dressings: 'Sandwich dressings', meats: 'Sandwich meats', meatVariants: 'Sandwich meat options',
  preparations: 'Sandwich preparation', styles: 'Sandwich styles', temperatures: 'Sandwich temperatures',
  toppings: 'Sandwich toppings', drinksData: 'Drinks', snacksData: 'Snacks', deliData: 'Deli menu prices',
  cateringData: 'Catering', specialCombinations: 'Sandwich combinations',
  'cateringData.trays': 'Catering trays', 'cateringData.boxedLunches': 'Catering boxed lunches',
  'cateringData.saladBowls': 'Catering salads', 'cateringData.saladBowlItems': 'Catering salads',
};
const meatNames = new Map(dataModules.meats.map(item => [item.id, item.name]));
const variantNames = new Map(dataModules.meatVariants.map(item => [item.id, `${meatNames.get(item.meat_id)} — ${item.name}`]));
const sortFields = fields => fields.sort((a, b) => a.category.localeCompare(b.category) || a.label.localeCompare(b.label));
const DELI_KEYS = { deliMeats: 'meats', deliCheeses: 'cheeses', deliBreads: 'breads', deliCondiments: 'condiments', deliSmokedFish: 'fish', deliHomemadeSalads: 'homemadeSalads', deliSaladPlates: 'saladPlates', deliSoups: 'soups', deliSpecialties: 'specialties', deliTreats: 'treats', deliFridgeItems: 'fridgeItems' };

export const adminAvailabilityFields = overrides => sortFields(Object.entries(dataModules)
  .filter(([moduleKey]) => moduleKey !== 'deliData')
  .flatMap(([moduleKey, data]) => collectAvailabilityFields(moduleKey, appendCustomItems(moduleKey, data, overrides), overrides))
  .map(field => ({ ...field, category: LABELS[field.moduleKey] || field.category,
    label: field.moduleKey === 'meatVariants' ? variantNames.get(field.itemId) || field.label : field.label,
    brandId: field.moduleKey === 'drinksData' ? drinksData.find(item => item.id === field.itemId)?.brandId :
      field.moduleKey === 'snacksData' ? snacksData.find(item => item.id === field.itemId)?.brandId : '',
  })));

export const adminPriceFields = overrides => sortFields(Object.entries(dataModules)
  // Existing deli prices are consumed under deliData.*. Do not offer duplicate
  // controls for module paths which the customer deli page never reads.
  .flatMap(([moduleKey, data]) => collectPriceFields(moduleKey, appendCustomItems(moduleKey, DELI_KEYS[moduleKey] ? [] : data, overrides), overrides)
    .map(field => {
      const index = Number(field.path.split('.')[1]);
      const variant = moduleKey === 'meatVariants' ? data[index] : null;
      const combo = moduleKey === 'specialCombinations' ? data[index] : null;
      return { ...field, moduleKey, category: LABELS[moduleKey] || field.category,
        label: variant ? `${variantNames.get(variant.id)} — Price adjustment` : combo ? `${meatNames.get(combo.primary_meat_id)} + ${meatNames.get(combo.secondary_meat_id)} — Combo price` : field.label };
    })));

export const featureChoices = overrides => {
  const deli = applyOverridesToData('deliData', dataModules.deliData, overrides);
  return [...Object.keys(ITEM_CATEGORIES), 'drinksData', 'snacksData'].flatMap(moduleKey =>
  (DELI_KEYS[moduleKey] ? [...deli[DELI_KEYS[moduleKey]], ...applyOverridesToData(moduleKey, [], overrides)] : applyOverridesToData(moduleKey, dataModules[moduleKey], overrides)).map(item => ({
    ...item, featureId: `${moduleKey}.${item.id}`, moduleKey, category: LABELS[moduleKey],
    available: isItemAvailable(moduleKey, item.id, overrides),
    href: moduleKey === 'drinksData' ? `/drinks#brand-${item.brandId}` :
      moduleKey === 'snacksData' ? `/snacks#brand-${item.brandId}` :
      moduleKey.startsWith('deli') ? '/deli' : '/menu#specialty-sandwiches',
  })))
  .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
};

export { drinkBrands, snackBrands };
